import {
  AlertIcon, CheckIcon, CircleIcon, CommentIcon, DotFillIcon, FileDiffIcon, GoalIcon, HistoryIcon, PeopleIcon, PersonIcon, PlayIcon, SkipIcon, XIcon,
} from "@primer/octicons-react";
import { rp } from "../api/client";
import { useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useAction, useChange, useCi, useCommit, useDecision, useDiff, useOverlaps, usePolicy, useRelated, useWhoami } from "../api/hooks";
import { canIn, OPEN_STATES, type Actor, type Attempt, type AuditEntry, type Change, type ChangeComment, type Decision, type Evidence, type Gate } from "../api/types";
import { describeAudit } from "../components/AuditEvent";
import { ChangeStatus } from "../components/ChangeStatus";
import { Checks } from "../components/Checks";
import { Avatar, CopyButton, DescriptionList, ErrorMessage, Pre, Query, Sha, TimeAgo } from "../components/common";
import { Annotations } from "../components/Details";
import { DiffView } from "../components/Diff";
import { duration } from "../components/format";
import { Markdown } from "../components/Markdown";
import { OutcomeReport } from "../components/OutcomeReport";
import { Screenshots } from "../components/Screenshots";
import { StateLabel, TierLabel } from "../components/StateLabel";
import { Alert, AlertTitle } from "../components/ui/alert";
import { Badge, Counter, type Tone } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { TabLabel, Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { explainTier, latestRun, mentionedFiles, splitMessage } from "../lib/supervise";
import { cn } from "../lib/utils";

/** Only people review, and never the agent that wrote the change (the server enforces both). */
const isHuman = (me: Actor | null | undefined) => !!me && (!!me.user || me.kind === "admin");

const TABS = ["outcome", "activity", "checks", "files"];

export function ChangeDetail() {
  const { repo = "", id = "" } = useParams();
  const q = useChange(repo, id);
  const d = useDecision(repo, id, q.data?.change.state);
  const me = useWhoami();
  const [params, setParams] = useSearchParams();
  // A change with an outcome report opens on it: what it proves comes before how it is written.
  const report = d.data?.outcomes;
  const first = report ? "outcome" : "activity";
  const asked = params.get("tab") ?? "";
  const tab = TABS.includes(asked) && (asked !== "outcome" || report) ? asked : first;
  return (
    <Query q={q}>
      {({ change: c, evidence, audit, comments }) => {
        const run = latestRun(evidence);
        const passed = run.filter((e) => e.status === "pass").length;
        const gating = run.filter((e) => e.status !== "skipped").length;
        const canReview = c.state === "review" && isHuman(me.data) && (me.data?.sub !== c.agent_id || !!d.data?.self_review) && canIn(me.data, "write", repo);
        const { subject } = splitMessage(c.message);
        return (
          <>
            <h1 className="mb-2 mt-6 text-3xl font-normal wrap-break-word">
              {subject} <span className="font-light text-muted-foreground">#{c.id.slice(-8)}</span>
            </h1>
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-muted-foreground">
              <StateLabel state={c.state} />
              <Avatar name={c.agent_id} size={20} agent={c.actor_kind === "agent"} />
              <b className="text-foreground">{c.agent_id}</b>
              <span>
                {c.paths.length} file{c.paths.length === 1 ? "" : "s"} into <code>main</code> · opened <TimeAgo ms={c.created} />
              </span>
            </div>
            <ChangeStatus repo={repo} c={c} run={run} audit={audit} canReview={canReview} decision={d.data} />
            <DecisionPanel repo={repo} c={c} d={d.data} canReview={canReview} />
            <Tabs value={tab} onValueChange={(v) => setParams(v === first ? {} : { tab: v }, { replace: true })}>
              <TabsList className="mb-0 mt-5 shadow-[inset_0_-1px_0_var(--color-border)]">
                {report && (
                  <TabsTrigger value="outcome">
                    <GoalIcon /> <TabLabel>Outcome</TabLabel>{" "}
                    <Counter>
                      {report.proven}/{report.outcomes.length}
                    </Counter>
                  </TabsTrigger>
                )}
                <TabsTrigger value="activity">
                  <CommentIcon /> <TabLabel>Activity</TabLabel> <Counter>{audit.length + (comments?.length ?? 0) + 1}</Counter>
                </TabsTrigger>
                <TabsTrigger value="checks">
                  <PlayIcon /> <TabLabel>Checks</TabLabel>{" "}
                  <Counter>
                    {passed}/{gating}
                  </Counter>
                </TabsTrigger>
                <TabsTrigger value="files">
                  <FileDiffIcon /> <TabLabel>Files changed</TabLabel> <Counter>{c.paths.length}</Counter>
                </TabsTrigger>
              </TabsList>
              {report && (
                <TabsContent value="outcome">
                  <OutcomeReport repo={repo} r={report} onChecks={() => setParams({ tab: "checks" }, { replace: true })} />
                </TabsContent>
              )}
              <TabsContent value="files">
                <Screenshots repo={repo} run={run} />
                <Files repo={repo} c={c} />
              </TabsContent>
              {tab !== "files" && tab !== "outcome" && (
                <div className="grid grid-cols-[minmax(0,1fr)_280px] gap-6 max-md:grid-cols-[minmax(0,1fr)]">
                  <div className="min-w-0">
                    <TabsContent value="activity">
                      <Activity repo={repo} c={c} evidence={evidence} audit={audit} comments={comments} />
                    </TabsContent>
                    <TabsContent value="checks">
                      <ChecksTab repo={repo} evidence={evidence} />
                    </TabsContent>
                  </div>
                  <Sidebar repo={repo} c={c} evidence={evidence} />
                </div>
              )}
            </Tabs>
          </>
        );
      }}
    </Query>
  );
}

function ChecksTab({ repo, evidence }: { repo: string; evidence: Evidence[] }) {
  const ci = useCi(repo);
  return <Checks repo={repo} evidence={evidence} totals={ci.data?.totals} />;
}

function SideSection({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <div className="border-b border-border-muted py-4 text-xs first:pt-0 last:border-b-0">
      <h4 className="mb-2 flex items-center gap-1 text-xs font-semibold text-muted-foreground">{title}</h4>
      {children}
    </div>
  );
}

const KIND: Record<string, string> = { agent: "Agent", admin: "Admin token", user: "Person", app: "App" };

function Sidebar({ repo, c, evidence }: { repo: string; c: Change; evidence: Evidence[] }) {
  const me = useWhoami();
  const act = useAction(repo);
  const [reason, setReason] = useState<string | null>(null);
  const canCancel = ["created", "ci", "review", "ready", "conflict"].includes(c.state) && (me.data?.sub === c.agent_id || canIn(me.data, "admin", repo));
  const landed = c.rebased_sha ?? (["landed", "verified", "reverted"].includes(c.state) ? c.commit_sha : null);
  const canRevert = canIn(me.data, "admin", repo) && landed && ["landed", "verified"].includes(c.state);
  const policy = usePolicy(repo);
  const why = policy.data ? explainTier(c.paths, policy.data) : null;
  const ciMs = evidence.reduce((n, e) => n + (e.ms ?? 0), 0);
  const end = ["landed", "verified", "reverted", "rejected", "cancelled"].includes(c.state) ? c.updated : Date.now();
  const kind = c.actor_kind === c.agent_id ? null : (KIND[c.actor_kind] ?? c.actor_kind);
  return (
    <aside className="flex flex-col pt-4">
      <SideSection title="Who wrote it">
        <div className="flex items-center gap-2">
          <Avatar name={c.agent_id} size={32} agent={c.actor_kind === "agent"} />
          <div className="flex min-w-0 flex-col">
            <b className="truncate text-sm">{c.agent_id}</b>
            <span className="truncate text-muted-foreground">
              {kind}
              {c.assisted_by && ` · via ${c.assisted_by}`}
            </span>
          </div>
        </div>
        <DescriptionList className="mt-3">
          {c.task_id && (
            <>
              <dt>Task</dt>
              <dd className="truncate" title={c.task_id}>
                <TaskRef task={c.task_id} />
              </dd>
            </>
          )}
          {c.owner && c.owner !== c.agent_id && (
            <>
              <dt>Owner</dt>
              <dd className="truncate" title="Person accountable for this agent">
                {c.owner}
              </dd>
            </>
          )}
          <dt>Open for</dt>
          <dd>{duration(Math.max(0, end - c.created))}</dd>
          {ciMs > 0 && (
            <>
              <dt>CI time</dt>
              <dd title="Sum of every job's run time">{duration(ciMs)}</dd>
            </>
          )}
          {(c.rebases ?? 0) > 0 && (
            <>
              <dt>Reruns</dt>
              <dd title="CI reran after rebasing onto a newer main">{c.rebases}</dd>
            </>
          )}
          {c.approved_by && (
            <>
              <dt>Approved by</dt>
              <dd className="truncate">{c.approved_by}</dd>
            </>
          )}
          {c.after && (
            <>
              <dt>After</dt>
              <dd className="truncate font-mono" title="Stacked: lands only after this change">
                <Link className="hover:text-link hover:underline" to={`${rp(repo)}/changes/${c.after}`}>
                  #{c.after.slice(-8)}
                </Link>
              </dd>
            </>
          )}
        </DescriptionList>
      </SideSection>
      <SideSection title="Risk">
        <TierLabel tier={c.risk_tier} />
        {why && <TierReason why={why} tier={c.risk_tier} docReview={Array.isArray(c.reason?.doc_review) ? (c.reason.doc_review as string[]) : []} />}
      </SideSection>
      {OPEN_STATES.includes(c.state) && <NearbyWork repo={repo} c={c} />}
      <SideSection title="Commits">
        <DescriptionList>
          <dt>Base</dt>
          <dd>
            <Sha repo={repo} sha={c.base_sha} n={8} />
          </dd>
          <dt>Change</dt>
          <dd>
            <Sha repo={repo} sha={c.commit_sha} n={8} />
          </dd>
          {landed && (
            <>
              <dt>Landed</dt>
              <dd>
                <Sha repo={repo} sha={landed} n={8} />
              </dd>
            </>
          )}
          <dt>Change id</dt>
          <dd className="flex items-center gap-0.5">
            <span className="font-mono">{c.id.slice(-8)}</span>
            <CopyButton value={c.id} title="Copy change id" />
          </dd>
        </DescriptionList>
      </SideSection>
      {(canCancel || canRevert) && (
        <SideSection title="Actions">
          <div className="flex flex-col gap-2">
            {canCancel && (
              <Button size="sm" disabled={act.isPending} onClick={() => act.mutate({ path: `/v1/repos/${repo}/changes/${c.id}/cancel`, body: {} })}>
                <XIcon /> Cancel change
              </Button>
            )}
            {canRevert &&
              (reason === null ? (
                <Button size="sm" variant="danger" disabled={act.isPending} onClick={() => setReason("")}>
                  <HistoryIcon /> Revert on main
                </Button>
              ) : (
                <form
                  className="flex flex-col gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    act.mutate({ path: `/v1/repos/${repo}/revert`, body: { commit: landed, reason } }, { onSuccess: () => setReason(null) });
                  }}
                >
                  <Input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why revert? (recorded in audit)" />
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="danger" className="flex-1" disabled={act.isPending || !reason.trim()}>
                      <HistoryIcon /> Confirm revert
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setReason(null)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              ))}
          </div>
          {act.error ? <ErrorMessage error={act.error} /> : null}
        </SideSection>
      )}
    </aside>
  );
}

/** A GitHub-style reference (owner/repo#123) or URL links out; anything else is shown as text. */
function TaskRef({ task }: { task: string }) {
  if (/^https?:\/\//.test(task))
    return (
      <a className="text-link hover:underline" href={task} target="_blank" rel="noreferrer">
        {task.replace(/^https?:\/\//, "")}
      </a>
    );
  return <span className="font-mono">{task}</span>;
}

function TierReason({ why, tier, docReview }: { why: ReturnType<typeof explainTier>; tier: string; docReview: string[] }) {
  if (why.tier !== tier) return <p className="mt-2 text-muted-foreground">Policy changed since it was opened: these files are now {why.tier} risk.</p>;
  return (
    <div className="mt-2 flex flex-col gap-1.5 text-muted-foreground">
      {why.tier === "high" && (
        <>
          <span>High because these files match high-risk rules:</span>
          {why.matches.map((m) => (
            <span key={m.glob}>
              <code>{m.glob}</code> · {m.paths.length} file{m.paths.length === 1 ? "" : "s"}
            </span>
          ))}
        </>
      )}
      {why.tier === "low" && <span>Low because every file matches a low-risk rule ({why.matches.map((m) => m.glob).join(", ")}).</span>}
      {why.tier === "normal" && <span>Normal: no file matches a high-risk rule, and {why.notLow.length === 1 ? "one file is" : `${why.notLow.length} files are`} not low-risk.</span>}
      <span>{why.review || docReview.length > 0 ? "A person must approve it after CI." : "Lands on green CI without human review."}</span>
      {docReview.length > 0 && (
        <span>
          Review because it edits documents with checks, or the tests that prove them: {docReview.map((p, i) => (
            <span key={p}>
              {i > 0 && ", "}
              <code>{p}</code>
            </span>
          ))}
        </span>
      )}
      {why.protectedHits.length > 0 && <span className="text-danger">Touches protected paths: {why.protectedHits.join(", ")}</span>}
    </div>
  );
}

/** Other agents on the same files, duplicate work on the task, and files that usually change with these. */
function NearbyWork({ repo, c }: { repo: string; c: Change }) {
  const overlaps = useOverlaps(repo, c, true);
  const related = useRelated(repo, c.paths);
  const missing = (related.data ?? []).filter((r) => r.ratio >= 0.3 && !c.paths.includes(r.path)).slice(0, 5);
  const list = overlaps.data ?? [];
  if (!list.length && !missing.length) return null;
  return (
    <SideSection
      title={
        <>
          <PeopleIcon size={12} /> Nearby work
        </>
      }
    >
      {list.map((o) => (
        <div key={o.change_id} className="mb-2">
          <div className="flex items-center gap-1.5">
            {o.same_task && <Badge tone="orange">same task</Badge>}
            <Link className="truncate font-semibold text-foreground hover:text-link" to={`${rp(repo)}/changes/${o.change_id}`} title={o.message}>
              {o.message || `#${o.change_id.slice(-8)}`}
            </Link>
          </div>
          <div className="text-muted-foreground">
            {o.agent} · {o.state} · {o.paths.length ? `${o.paths.length} shared file${o.paths.length === 1 ? "" : "s"}` : "same task"}
          </div>
        </div>
      ))}
      {missing.length > 0 && (
        <div className={cn(list.length > 0 && "mt-3")}>
          <div className="mb-1 text-muted-foreground">Usually changes with these files, but not in this change:</div>
          {missing.map((r) => (
            <div key={r.path} className="truncate font-mono" title={`${Math.round(r.ratio * 100)}% of changes to these files also touched it`}>
              {r.path} <span className="text-faint">{Math.round(r.ratio * 100)}%</span>
            </div>
          ))}
        </div>
      )}
    </SideSection>
  );
}

const BADGE: Record<Tone, string> = {
  default: "bg-accent text-muted-foreground",
  green: "bg-success-emphasis text-on-emphasis",
  purple: "bg-merged-emphasis text-on-emphasis",
  red: "bg-danger-emphasis text-on-emphasis",
  orange: "bg-accent text-warning",
  yellow: "bg-accent text-warning",
  blue: "bg-accent text-info",
};

/** One entry on the discussion timeline: a round badge on the rail, then the content. */
function TimelineItem({ badge, tone = "default", children }: { badge: ReactNode; tone?: Tone | "avatar"; children: ReactNode }) {
  return (
    <div className="relative pt-4">
      <div
        className={cn(
          "absolute -left-10 top-3 grid size-8 place-items-center rounded-full",
          tone === "avatar" ? "bg-transparent" : cn("border-2 border-background", BADGE[tone]),
        )}
      >
        {badge}
      </div>
      {children}
    </div>
  );
}

type Entry = { ts: number; key: string; node: ReactNode };

function Activity({ repo, c, evidence, audit, comments }: { repo: string; c: Change; evidence: Evidence[]; audit: AuditEntry[]; comments?: ChangeComment[] }) {
  const { body, trailers } = splitMessage(c.message);
  const files = mentionedFiles(body, c.paths);
  const ci = useCi(repo);
  const entries: Entry[] = [
    ...audit.map((a) => {
      const ev = describeAudit(a, repo);
      return {
        ts: a.ts,
        key: `a${a.seq}`,
        node: (
          <TimelineItem tone={ev.tone} badge={<ev.Icon size={14} />}>
            <div className="flex min-h-8 flex-wrap items-center gap-1.5 pt-1 text-muted-foreground">
              <Avatar name={a.actor} size={18} />
              <b className="text-foreground">{a.actor}</b> {ev.text}
              <span className="text-xs text-faint">
                <TimeAgo ms={a.ts} />
              </span>
            </div>
          </TimelineItem>
        ),
      };
    }),
    ...(comments ?? []).map((m) => ({ ts: m.ts, key: `c${m.id}`, node: <CommentItem m={m} /> })),
  ].sort((a, b) => a.ts - b.ts);
  return (
    <div className="relative mt-4 pl-10 before:absolute before:inset-y-0 before:left-[15px] before:w-0.5 before:bg-border-muted">
      <TimelineItem tone="avatar" badge={<Avatar name={c.agent_id} size={32} agent={c.actor_kind === "agent"} />}>
        <div className="rounded-md border bg-background">
          <div className="flex items-center gap-1.5 rounded-t-md border-b bg-muted px-4 py-2 text-muted-foreground">
            <b className="text-foreground">{c.agent_id}</b> described the change <TimeAgo ms={c.created} />
          </div>
          <div className="p-4">{body ? <Markdown source={body} /> : <span className="text-muted-foreground">No description provided.</span>}</div>
          {files.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-border-muted px-4 py-2 text-xs text-muted-foreground">
              Files it mentions:
              {files.map((f) =>
                f.path ? (
                  <Link key={f.name} to={`?tab=files#diff-${f.path}`} className="inline-flex items-center gap-1 rounded-md bg-success-subtle px-1.5 py-0.5 font-mono text-success hover:underline" title={`Changed: ${f.path}`}>
                    <CheckIcon size={12} /> {f.name}
                  </Link>
                ) : (
                  <span key={f.name} className="inline-flex items-center gap-1 rounded-md bg-warning-subtle px-1.5 py-0.5 font-mono text-warning" title="Mentioned but not changed by this change">
                    <CircleIcon size={12} /> {f.name}
                  </span>
                ),
              )}
            </div>
          )}
          {trailers.length > 0 && (
            <div className="flex flex-wrap gap-1.5 border-t border-border-muted px-4 py-2">
              {trailers.map(([k, v]) => (
                <Badge key={k + v} title={`${k}: ${v}`}>
                  <span className="text-muted-foreground">{k.replace(/^Bee-/, "")}</span> <span className="max-w-60 truncate font-mono">{v}</span>
                </Badge>
              ))}
            </div>
          )}
        </div>
      </TimelineItem>
      {entries.map((e) => (
        <div key={e.key}>{e.node}</div>
      ))}
      {c.reason && !WAITING.has(c.reason.code) && <Reason repo={repo} reason={c.reason} />}
      {evidence.length > 0 && (
        <TimelineItem badge={<PlayIcon size={14} />}>
          <Checks repo={repo} evidence={evidence} totals={ci.data?.totals} compact />
        </TimelineItem>
      )}
      {comments && <Composer repo={repo} c={c} />}
    </div>
  );
}

function CommentItem({ m }: { m: ChangeComment }) {
  return (
    <TimelineItem tone="avatar" badge={<Avatar name={m.app ?? m.actor} size={32} />}>
      <div className="rounded-md border bg-background">
        <div className="flex items-center gap-1.5 rounded-t-md border-b bg-muted px-4 py-2 text-muted-foreground">
          <b className="text-foreground">{m.app ?? m.actor}</b> commented <TimeAgo ms={m.ts} />
        </div>
        <div className="p-4">
          <Markdown source={m.body} />
        </div>
      </div>
    </TimelineItem>
  );
}

/** Instructions for the agent: a comment it sees on its next bee show, as data rather than commands. */
function Composer({ repo, c }: { repo: string; c: Change }) {
  const me = useWhoami();
  const act = useAction(repo);
  const [text, setText] = useState("");
  if (!me.data || !canIn(me.data, "write", repo)) return null;
  return (
    <TimelineItem tone="avatar" badge={<Avatar name={me.data.sub} size={32} />}>
      <form
        className="rounded-md border bg-background"
        onSubmit={(e) => {
          e.preventDefault();
          act.mutate({ path: `/v1/repos/${repo}/changes/${c.id}/comments`, body: { body: text } }, { onSuccess: () => setText("") });
        }}
      >
        <textarea
          className="block min-h-20 w-full resize-y rounded-t-md bg-transparent px-4 py-3 text-sm outline-none placeholder:text-faint"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Tell ${c.agent_id} what to change. It reads comments on its next bee show.`}
          aria-label="Comment for the agent"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && text.trim()) e.currentTarget.form?.requestSubmit();
          }}
        />
        <div className="flex items-center gap-2 border-t border-border-muted px-3 py-2">
          <span className="text-xs text-muted-foreground">Markdown supported · ⌘↵ to send</span>
          <Button size="sm" variant="primary" className="ml-auto" disabled={act.isPending || !text.trim()}>
            Comment
          </Button>
        </div>
        {act.error ? <ErrorMessage error={act.error} /> : null}
      </form>
    </TimelineItem>
  );
}

const GATE_KIND: Record<Gate["kind"], string> = { ci: "CI", check: "App check", docs: "Doc check", review: "Review", parent: "Stacked on" };

const GATE_ICON: Record<Gate["state"], ReactNode> = {
  pass: <CheckIcon size={16} className="text-success" aria-label="passed" />,
  fail: <XIcon size={16} className="text-danger" aria-label="failed" />,
  pending: <DotFillIcon size={16} className="text-warning" aria-label="pending" />,
  waiting: <PersonIcon size={16} className="text-warning" aria-label="waiting" />,
  skipped: <SkipIcon size={16} className="text-muted-foreground" aria-label="skipped" />,
};

/** The decision under the status bar: every gate the tier sets and why, review actions, then the attempts. */
function DecisionPanel({ repo, c, d, canReview }: { repo: string; c: Change; d?: Decision; canReview: boolean }) {
  if (!d || d.state !== c.state) return null;
  const done = (g: Gate) => g.state === "pass" || g.state === "skipped";
  const open = d.gates.filter((g) => !done(g));
  const passed = d.gates.filter(done);
  const attempts = d.attempts ?? [];
  if (!d.gates.length && attempts.length < 2) return null;
  return (
    <section className="mt-3 rounded-md border bg-background" aria-label="Gates">
      {open.length > 0 && (
        <ul className="m-0 list-none p-0">
          {open.map((g) => (
            <GateRow key={g.kind + g.name} repo={repo} g={g} />
          ))}
        </ul>
      )}
      {passed.length > 0 && (
        <details className="group border-b border-border-muted last:border-b-0">
          <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-2 text-muted-foreground">
            <CheckIcon size={16} className="text-success" />
            <span>
              {passed.length} {open.length ? "passed" : `gate${passed.length === 1 ? "" : "s"}, all passed`}
            </span>
            <span className="min-w-0 truncate text-xs text-faint group-open:hidden">{passed.map((g) => g.name).join(", ")}</span>
          </summary>
          <ul className="m-0 list-none p-0">
            {passed.map((g) => (
              <GateRow key={g.kind + g.name} repo={repo} g={g} />
            ))}
          </ul>
        </details>
      )}
      <ReviewActions repo={repo} c={c} d={d} canReview={canReview} />
      {attempts.length > 1 && <Attempts repo={repo} c={c} list={attempts} />}
    </section>
  );
}

function GateRow({ repo, g }: { repo: string; g: Gate }) {
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-border-muted px-4 py-2 last:border-b-0">
      {GATE_ICON[g.state]}
      <span className="text-xs text-muted-foreground">{GATE_KIND[g.kind]}</span>
      {g.kind === "parent" ? (
        <Link className="font-mono" to={`${rp(repo)}/changes/${g.name}`}>
          #{g.name.slice(-8)}
        </Link>
      ) : (
        <b className="min-w-0 wrap-break-word">{g.name}</b>
      )}
      {g.why && <span className="min-w-0 text-xs text-muted-foreground wrap-break-word">{g.why}</span>}
    </li>
  );
}

/** Approve or reject in place, so a reviewer decides without scrolling. Only people review, never the author. */
function ReviewActions({ repo, c, d, canReview }: { repo: string; c: Change; d: Decision; canReview: boolean }) {
  const me = useWhoami();
  const act = useAction(repo);
  const [rejectWhy, setRejectWhy] = useState<string | null>(null);
  // A tier that needs review accepts approval while CI still runs: it then skips review once green.
  const review = d.gates.find((g) => g.kind === "review");
  const mine = me.data?.sub === c.agent_id && !d.self_review;
  const canPreapprove = review?.state === "pending" && ["created", "ci"].includes(c.state) && !c.approved_by && isHuman(me.data) && !mine && canIn(me.data, "write", repo);
  if (c.state === "review" && mine)
    return (
      <div className="border-b border-border-muted bg-warning-subtle px-4 py-3 text-muted-foreground last:border-b-0">
        You opened this change, so another person with write access must approve it. To approve your own changes, set <code>allow_self_review: true</code> in <code>.bee/policy.yaml</code>.
      </div>
    );
  if (!canReview && !canPreapprove) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border-muted bg-warning-subtle px-4 py-3 last:border-b-0">
      <span className="mr-auto text-muted-foreground">
        {canReview ? (d.outcomes ? "Check the outcome." : "Read the files below, then approve or reject.") : "Approve now and it lands as soon as CI passes."}
      </span>
      {rejectWhy === null ? (
        <>
          <Button size="sm" variant="primary" disabled={act.isPending} onClick={() => act.mutate({ path: `/v1/repos/${repo}/changes/${c.id}/approve`, body: {} })}>
            <CheckIcon /> {canReview ? "Approve and land" : "Approve once CI passes"}
          </Button>
          {canReview && (
            <Button size="sm" variant="danger" disabled={act.isPending} onClick={() => setRejectWhy("")}>
              <XIcon /> Reject
            </Button>
          )}
        </>
      ) : (
        <form
          className="flex w-full flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            act.mutate({ path: `/v1/repos/${repo}/changes/${c.id}/reject`, body: { reason: rejectWhy } }, { onSuccess: () => setRejectWhy(null) });
          }}
        >
          <Input autoFocus className="min-w-48 flex-1" value={rejectWhy} onChange={(e) => setRejectWhy(e.target.value)} placeholder="Why? (the agent reads this)" />
          <Button size="sm" variant="danger" disabled={act.isPending || !rejectWhy.trim()}>
            <XIcon /> Confirm reject
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setRejectWhy(null)}>
            Cancel
          </Button>
        </form>
      )}
      {act.error ? <ErrorMessage error={act.error} /> : null}
    </div>
  );
}

/** Each re-land after a rejection is a new change for the same task: one line, with the diff since the last attempt; the loop expands. */
function Attempts({ repo, c, list }: { repo: string; c: Change; list: Attempt[] }) {
  const [diff, setDiff] = useState(false);
  const i = Math.max(0, list.findIndex((a) => a.current));
  const prev = i > 0 ? list[i - 1] : null;
  const rebases = c.rebases ?? 0;
  return (
    <div className="px-4 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <details className="group min-w-0 flex-1">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-muted-foreground">
            <HistoryIcon size={16} />
            <span>
              <b className="text-foreground">
                Attempt {i + 1} of {list.length}
              </b>{" "}
              for task {c.task_id}
              {rebases > 0 && ` · ${rebases} rerun${rebases === 1 ? "" : "s"} after rebase`}
            </span>
          </summary>
          <ol className="m-0 mt-2 list-none p-0">
            {list.map((a, k) => (
              <AttemptRow key={a.change_id} repo={repo} a={a} n={k + 1} />
            ))}
          </ol>
        </details>
        {prev && (
          <Button size="sm" variant="ghost" onClick={() => setDiff(!diff)} aria-expanded={diff}>
            <FileDiffIcon /> {diff ? "Hide" : "What changed since attempt"} {i}
          </Button>
        )}
      </div>
      {diff && prev && <AttemptDiff repo={repo} from={prev.commit} to={list[i].commit} />}
    </div>
  );
}

function AttemptRow({ repo, a, n }: { repo: string; a: Attempt; n: number }) {
  const why = a.failed.length ? `failed: ${a.failed.join(", ")}` : a.code;
  return (
    <li className={cn("flex flex-wrap items-center gap-2 border-t border-border-muted py-1.5", a.current && "bg-accent/40")}>
      <span className="w-5 text-right text-xs text-muted-foreground">{n}.</span>
      <StateLabel state={a.state} />
      {a.current ? <b>this change</b> : <Link to={`${rp(repo)}/changes/${a.change_id}`}>#{a.change_id.slice(-8)}</Link>}
      <span className="text-xs text-muted-foreground">
        <TimeAgo ms={a.created} />
      </span>
      {why && ["rejected", "reverted", "conflict"].includes(a.state) && <span className="text-xs text-muted-foreground">{why}</span>}
      {a.passed.length > 0 && <span className="text-xs text-faint">passed: {a.passed.join(", ")}</span>}
    </li>
  );
}

function AttemptDiff({ repo, from, to }: { repo: string; from: string; to: string }) {
  const q = useDiff(repo, from, to);
  return <Query q={q}>{(d) => (d.files.length ? <DiffView files={d.files} refs={{ repo, from, to }} /> : <p className="ml-7 mt-2 text-xs text-muted-foreground">No file differences.</p>)}</Query>;
}

/** Reasons that only mean "waiting": the status bar and the audit trail already say so. */
const WAITING = new Set(["MAIN_RED", "CHECKING_BASE"]);

function Reason({ repo, reason }: { repo: string; reason: any }) {
  const warning = reason.code === "CONFLICT" || (!reason.reverted && !reason.code);
  return (
    <TimelineItem tone={warning ? "orange" : "red"} badge={<AlertIcon size={14} />}>
      <Alert variant={warning ? "warning" : "error"} className="mt-0">
        {reason.message && (
          <AlertTitle>
            {reason.code}: {reason.message}
          </AlertTitle>
        )}
        {reason.hint && <div className="text-muted-foreground">{reason.hint}</div>}
        {reason.failed?.map((f: any) => (
          <div key={f.job ?? f.name} className="mt-2">
            <b>{f.job ?? f.name}</b> <span className="text-muted-foreground">{f.status}</span>
            <Annotations summary={f.summary} />
          </div>
        ))}
        {reason.conflicts_with?.map((w: any) => (
          <div key={w.change_id} className="mt-2">
            landed first by <b>{w.agent}</b> in{" "}
            <Link className="font-mono" to={`${rp(repo)}/changes/${w.change_id}`}>
              {w.change_id.slice(-8)}
            </Link>
            {w.message && <span className="text-muted-foreground"> {w.message.split("\n", 1)[0]}</span>}
          </div>
        ))}
        {reason.conflicts?.map((f: any) => (
          <div key={f.path} className="mt-2">
            <b className="font-mono">{f.path}</b> <Badge>{f.kind}</Badge>
            {f.hunks?.map((h: any, i: number) => (
              <Pre key={i}>{`@@ line ${h.line}\n<<<<<<< main\n${h.main.join("\n")}\n||||||| base\n${h.base.join("\n")}\n=======\n${h.change.join("\n")}\n>>>>>>> change`}</Pre>
            ))}
          </div>
        ))}
        {reason.reverted && (
          <div className="mt-2">
            Reverted by <Sha repo={repo} sha={reason.reverted.by} />. {reason.reverted.hint}
            {Array.isArray(reason.reverted.failing) &&
              reason.reverted.failing.map((f: any, i: number) => (
                <div key={f.job ?? i} className="mt-2">
                  <b>{f.job}</b> {f.status && <span className="text-muted-foreground">{f.status}</span>}
                  {f.summary && <Annotations summary={f.summary} />}
                </div>
              ))}
          </div>
        )}
      </Alert>
    </TimelineItem>
  );
}

/** Rebased changes show the landed commit's diff; others diff base..commit. */
function Files({ repo, c }: { repo: string; c: Change }) {
  const docsFirst = c.risk_tier === "high";
  if (c.rebased_sha) return <CommitDiff repo={repo} sha={c.rebased_sha} docsFirst={docsFirst} />;
  return <RangeDiff repo={repo} from={c.base_sha} to={c.ci_sha} docsFirst={docsFirst} />;
}

function RangeDiff({ repo, from, to, docsFirst }: { repo: string; from: string; to: string; docsFirst: boolean }) {
  const q = useDiff(repo, from, to);
  return <Query q={q}>{(d) => <DiffView files={d.files} refs={{ repo, from, to }} docsFirst={docsFirst} tree />}</Query>;
}

function CommitDiff({ repo, sha, docsFirst }: { repo: string; sha: string; docsFirst: boolean }) {
  const q = useCommit(repo, sha);
  return <Query q={q}>{(d) => <DiffView files={d.files ?? []} refs={{ repo, from: d.parents[0], to: sha }} docsFirst={docsFirst} tree />}</Query>;
}